import React, { useRef } from 'react';
import WithVisibilityObserver from '@gumtree/shared/src/util/element-visibility/visibility-observer';
import { trackGA4Event } from '@gumtree/shared/src/util/ga4-shared';
import { getLinkHostname } from '@gumtree/shared/src/util/ga4-bff';

import { Tab, Tabs } from '@gumtree/ui-library';
import { qaAttribute } from '@gumtree/ui-library/src/utils/qa-service';
import TopLocations from './top-locations';
import TopSearches from './top-searches';
import TopSearchCondensed from './top-search-condensed';
import ErrorBoundary from '../error-boundary';

import { BrowseLinksContainer } from './style';
import { getClinetPageCategories } from '../utils';

const BrowseLinks = ({ pageType = 'Homepage', browseLinks = null }: BrowseLinksProps) => {
    const hasShowLocation = useRef(false);

    if (!browseLinks) return null;

    const { topLocations, topCities, topSearches, topSearchCondensed } = browseLinks;

    const locationItems = pageType === 'Homepage' ? topLocations : topCities;

    const showRenderTopLocation =
        Array.isArray(locationItems) && locationItems.some((location) => location.items.length);

    const showTopSearch = () => {
        const listLinkDetails =
            (topSearches
                ? topSearches
                      .reduce((pre, next) => pre.concat(next.sections), [])
                      .reduce((pre, next) => pre.concat(next.items), [])
                : topSearchCondensed.reduce((pre, next) => pre.concat(next.items), [])) || [];
        trackGA4Event<GA4.BrowseLinksEvent>({
            event: 'link_impress',
            linkLocation: 'topSearches',
            linkType: 'inside',
            linkURL: listLinkDetails.map((i) => i.url).join(','),
            linkText: listLinkDetails.map((i) => i.name || i.label).join(','),
            linkDomain: listLinkDetails.map((i) => getLinkHostname(i.url)).join(','),
            linkDetail: {},
            listLinkDetail: getClinetPageCategories(),
        });
    };

    const showLocation = () => {
        if (hasShowLocation.current) return;
        const listLinkDetails = (locationItems || []).reduce(
            (pre, next) => pre.concat(next.items),
            []
        );
        trackGA4Event<GA4.BrowseLinksEvent>({
            event: 'link_impress',
            linkLocation: 'topLocations',
            linkType: 'inside',
            linkURL: listLinkDetails.map((i) => i.url).join(','),
            linkText: listLinkDetails.map((i) => i.name).join(','),
            linkDomain: listLinkDetails.map((i) => getLinkHostname(i.url)).join(','),
            linkDetail: {},
            listLinkDetail: getClinetPageCategories(),
        });
        hasShowLocation.current = true;
    };

    const onVisible = () => {
        const hasTopSearches = !!topSearches || !!topSearchCondensed;
        hasTopSearches ? showTopSearch() : showRenderTopLocation && showLocation();
    };

    const renderTopSearchTab = () => {
        if (topSearches) {
            return (
                <Tab title="Top searches" forceRender>
                    <TopSearches items={topSearches} />
                </Tab>
            );
        }
        if (topSearchCondensed) {
            return (
                <Tab title={topSearchCondensed[0].title} forceRender>
                    <TopSearchCondensed topSearchCondensed={topSearchCondensed} />
                </Tab>
            );
        }
        return null;
    };

    return (
        <ErrorBoundary>
            <WithVisibilityObserver onVisible={onVisible}>
                <BrowseLinksContainer isSection {...qaAttribute('browse-links-container')}>
                    <Tabs>
                        {renderTopSearchTab()}
                        {showRenderTopLocation && (
                            <Tab
                                title="Top Locations"
                                {...qaAttribute('top-locations-toggler')}
                                onClick={showLocation}
                                forceRender
                            >
                                <TopLocations
                                    items={locationItems}
                                    showTitle={pageType === 'Homepage'}
                                    {...qaAttribute('top-locations-content')}
                                />
                            </Tab>
                        )}
                    </Tabs>
                </BrowseLinksContainer>
            </WithVisibilityObserver>
        </ErrorBoundary>
    );
};

interface BrowseLinksProps {
    browseLinks: any;
    pageType: string;
}

export default BrowseLinks;
